import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AutoConfirmedAlert, ConfirmPassword } from '@adjust/dashboard-components';

import { connectWithStore } from '@common/connectWithStore';
import { SidebarId } from '@react/constants/sidebars';
import { changeEmail, closeAlert, resendEmail, getEditUserEmailDetails } from '@redux/ducks/userDetails/userEmail';
import { EditUserEmailForm } from './EditUserEmailForm';

const EditUserEmail = ({
  userId,
  currentAccountId,
  userEmail,
  unconfirmedEmail,
  isEmailOwner,
  changeEmail,
  resendEmail,
  closeAlert,
  onCancel,
  isLoading,
  trace,
  isAlertOpen,
}) => {
  // Close Alert when the component is unmounting
  useEffect(
    () => () => {
      if (isAlertOpen) {
        closeAlert();
      }
    },
    [isAlertOpen, closeAlert],
  );

  const [isConfirmPasswordOpen, setIsConfirmPasswordOpen] = useState(false);
  const [email, setEmail] = useState(userEmail);
  const { status, message } = trace;

  const onFormSave = newEmail => {
    setEmail(newEmail);
    setIsConfirmPasswordOpen(true);
  };

  const onPasswordConfirm = passwordPayload => {
    setIsConfirmPasswordOpen(false);
    changeEmail({
      ...passwordPayload,
      email,
      id: userId,
      accountId: currentAccountId,
    });
  };

  const onAlertConfirm = () => {
    if (trace.status === 'info') {
      onCancel();
    } else {
      closeAlert();
    }
  };

  const onResendEmail = () => {
    resendEmail({
      id: userId,
      accountId: currentAccountId,
    });
  };

  return (
    <>
      <EditUserEmailForm
        email={email}
        initialEmail={userEmail}
        isEmailOwner={isEmailOwner}
        unconfirmedEmail={unconfirmedEmail}
        resendConfirmationEmail={onResendEmail}
        onSave={onFormSave}
        onCancel={onCancel}
        isLoading={isLoading}
      />
      <ConfirmPassword
        parentSidebarId={SidebarId.UserDetails}
        isOpen={isConfirmPasswordOpen}
        onCancel={() => setIsConfirmPasswordOpen(false)}
        onConfirm={onPasswordConfirm}
      />
      <AutoConfirmedAlert
        parentSidebarId={SidebarId.UserDetails}
        isOpen={isAlertOpen}
        type={status}
        message={message}
        onConfirm={onAlertConfirm}
      />
    </>
  );
};

EditUserEmail.propTypes = {
  userId: PropTypes.number.isRequired,
  currentAccountId: PropTypes.number.isRequired,
  userEmail: PropTypes.string.isRequired,
  unconfirmedEmail: PropTypes.string,
  isEmailOwner: PropTypes.bool,
  changeEmail: PropTypes.func,
  resendEmail: PropTypes.func,
  closeAlert: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  trace: PropTypes.object,
  isAlertOpen: PropTypes.bool,
};

const mapStateToProps = state => {
  const { isChangingEmail, isAlertOpen, trace } = getEditUserEmailDetails(state);

  return {
    isLoading: isChangingEmail,
    trace,
    isAlertOpen,
  };
};

const mapDispatchToProps = {
  changeEmail,
  closeAlert,
  resendEmail,
};

export default connectWithStore(mapStateToProps, mapDispatchToProps)(EditUserEmail);

export { EditUserEmail };
